import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon, Input, Switch, Button, Breadcrumb } from "../../ui";
import {
  useDrivers,
  useDriverManagers,
  useBusinessesLite,
  useDriverCompanies,
  useCreateDeliveryGroup,
} from "../../../hooks";
import {
  DEFAULT_FORM_DATA,
  DELIVERY_GROUP_TYPES,
  AUTOASSIGN_FIELDS,
} from "./utils";

const CreateDeliveryGroupPage = () => {
  const { t } = useTranslation("delivery");
  const navigate = useNavigate();
  const createDeliveryGroupMutation = useCreateDeliveryGroup();

  // Form state
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  const [errors, setErrors] = useState({});
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [businessSearchInput, setBusinessSearchInput] = useState("");
  const [businessSearchTerm, setBusinessSearchTerm] = useState("");

  // Fetch data
  const { data: driverManagersData } = useDriverManagers("all", "", 1);
  const { data: driversData } = useDrivers(searchTerm);
  const { data: driverCompaniesData } = useDriverCompanies();
  const { data: businessesData, isLoading: isLoadingBusinesses } =
    useBusinessesLite({
      enabled: true,
      searchTerm: businessSearchTerm,
    });

  const driverManagers = driverManagersData?.users || [];
  const drivers = driversData?.drivers || [];
  const driverCompanies = driverCompaniesData?.driverCompanies || [];
  const businesses = businessesData || [];

  // Selected items state
  const [selectedDrivers, setSelectedDrivers] = useState(new Set());
  const [selectedDriverCompanies, setSelectedDriverCompanies] = useState(
    new Set(),
  );
  const [selectedBusinesses, setSelectedBusinesses] = useState(new Set());

  // Debounce search inputs
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setBusinessSearchTerm(businessSearchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [businessSearchInput]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t("createGroup.validation.nameRequired");
    }

    if (!formData.administrator_id) {
      newErrors.administrator_id = t(
        "createGroup.validation.deliveryManagerRequired",
      );
    }

    if (
      formData.type === DELIVERY_GROUP_TYPES.IN_HOUSE_DRIVERS &&
      selectedDrivers.size === 0
    ) {
      newErrors.drivers = t("createGroup.validation.atLeastOneDriver");
    }

    if (
      formData.type === DELIVERY_GROUP_TYPES.DELIVERY_COMPANIES &&
      selectedDriverCompanies.size === 0
    ) {
      newErrors.driver_companies = t(
        "createGroup.validation.atLeastOneDeliveryCompany",
      );
    }

    if (selectedBusinesses.size === 0) {
      newErrors.business = t("createGroup.validation.atLeastOneBusiness");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Build payload with only non-empty values
      const payload = {
        name: formData.name,
        administrator_id: parseInt(formData.administrator_id),
        type: formData.type,
        enabled: formData.enabled,
        business: Array.from(selectedBusinesses),
        ...AUTOASSIGN_FIELDS.reduce((acc, key) => {
          acc[key] = formData[key];
          return acc;
        }, {}),
      };

      if (formData.type === DELIVERY_GROUP_TYPES.IN_HOUSE_DRIVERS) {
        payload.drivers = Array.from(selectedDrivers);
      } else {
        payload.driver_companies = Array.from(selectedDriverCompanies);
      }

      if (
        formData.driver_available_max_distance !== null &&
        formData.driver_available_max_distance !== ""
      ) {
        payload.driver_available_max_distance =
          formData.driver_available_max_distance;
      }

      const result = await createDeliveryGroupMutation.mutateAsync(payload);
      navigate(`/delivery/groups/${result?.result?.id || ""}`, {
        state: { group: result?.result },
      });
    } catch (error) {
      console.error("Create delivery group error:", error);
    }
  };

  const handleCancel = () => {
    navigate("/delivery/groups");
  };

  // Generic toggle function for Set-based state
  const createToggleFunction = (setter) => (id) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Create toggle functions using the generic helper
  const toggleDriver = createToggleFunction(setSelectedDrivers);
  const toggleDriverCompany = createToggleFunction(setSelectedDriverCompanies);
  const toggleBusiness = createToggleFunction(setSelectedBusinesses);

  const breadcrumbItems = [
    {
      label: t("createGroup.breadcrumb.delivery"),
      path: "/delivery/groups",
      icon: "Truck",
    },
    {
      label: t("createGroup.breadcrumb.createDeliveryGroup"),
      active: true,
      disabled: true,
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="rounded-lg border border-gray-800 bg-gray-900 p-4 sm:p-6">
        <div>
          <h1 className="text-xl font-bold text-white sm:text-2xl">
            {t("createGroup.header.title")}
          </h1>
          <p className="mt-2 text-sm text-gray-400">
            {t("createGroup.header.subtitle")}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="rounded-lg border border-gray-800 bg-gray-900">
          {/* Basic Information */}
          <div className="border-b border-gray-800 p-3 sm:p-6">
            <h3 className="mb-4 flex items-center text-lg font-semibold text-white">
              <Icon name="Info" className="mr-2 h-5 w-5" />
              {t("createGroup.sections.basicInformation")}
            </h3>

            <div className="space-y-4">
              <Input
                label={t("createGroup.fields.name.label")}
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                error={errors.name}
                placeholder={t("createGroup.fields.name.placeholder")}
                required
              />

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-300">
                  {t("createGroup.fields.deliveryManager.label")}{" "}
                  <span className="text-red-400">*</span>
                </label>
                <select
                  value={formData.administrator_id}
                  onChange={(e) =>
                    handleInputChange("administrator_id", e.target.value)
                  }
                  className={`custom-select w-full rounded-lg border bg-gray-800 px-3 py-2 text-white ${
                    errors.administrator_id
                      ? "border-red-500"
                      : "border-gray-700"
                  } focus:ring-2 focus:ring-blue-500 focus:outline-none`}
                >
                  <option value="">
                    {t("createGroup.fields.deliveryManager.selectPlaceholder")}
                  </option>
                  {driverManagers.map((manager) => (
                    <option key={manager.id} value={manager.id}>
                      {manager.name} {manager.lastname}{" "}
                      {manager.email ? `(${manager.email})` : ""}
                    </option>
                  ))}
                </select>
                {errors.administrator_id && (
                  <p className="mt-1 text-xs text-red-400">
                    {errors.administrator_id}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-300">
                  {t("createGroup.fields.type.label")}{" "}
                  <span className="text-red-400">*</span>
                </label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    handleInputChange("type", parseInt(e.target.value))
                  }
                  className="custom-select w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value={DELIVERY_GROUP_TYPES.IN_HOUSE_DRIVERS}>
                    {t("groupTypes.inHouseDrivers")}
                  </option>
                  <option value={DELIVERY_GROUP_TYPES.DELIVERY_COMPANIES}>
                    {t("groupTypes.deliveryCompanies")}
                  </option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-300">
                  {t("createGroup.fields.enabled")}
                </label>
                <Switch
                  checked={formData.enabled}
                  onCheckedChange={(checked) =>
                    handleInputChange("enabled", checked)
                  }
                />
              </div>
            </div>
          </div>

          {/* Drivers Selection (when type = 0) */}
          {formData.type === DELIVERY_GROUP_TYPES.IN_HOUSE_DRIVERS && (
            <div className="border-b border-gray-800 p-3 sm:p-6">
              <h3 className="mb-4 flex items-center text-lg font-semibold text-white">
                <Icon name="Users" className="mr-2 h-5 w-5" />
                {t("createGroup.sections.selectDrivers")}
              </h3>

              <div className="space-y-4">
                <Input
                  label={t("createGroup.fields.searchDrivers.label")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={t(
                    "createGroup.fields.searchDrivers.placeholder",
                  )}
                />

                <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-700 bg-gray-800 p-4">
                  {drivers.length === 0 ? (
                    <p className="py-4 text-center text-sm text-gray-400">
                      {t("createGroup.states.noDriversFound")}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {drivers.map((driver) => {
                        const name =
                          driver.name && driver.lastname
                            ? `${driver.name} ${driver.lastname}`
                            : driver.name || t("createGroup.fallbacks.unknown");
                        const isSelected = selectedDrivers.has(driver.id);

                        return (
                          <div
                            key={driver.id}
                            className={`flex items-center justify-between rounded-lg border p-3 ${
                              isSelected
                                ? "border-blue-500 bg-blue-500/10"
                                : "border-gray-600 bg-gray-700/50"
                            }`}
                          >
                            <div className="flex flex-1 items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleDriver(driver.id)}
                                className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
                              />
                              {driver.photo ? (
                                <img
                                  src={driver.photo}
                                  alt={name}
                                  className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                    e.target.nextSibling.style.display = "flex";
                                  }}
                                />
                              ) : null}
                              <div
                                className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-600 ${
                                  driver.photo ? "hidden" : ""
                                }`}
                              >
                                <Icon
                                  name="User"
                                  className="h-4 w-4 text-gray-400"
                                />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-white">
                                  {name}
                                </p>
                                {driver.email && (
                                  <p className="text-xs text-gray-400">
                                    {driver.email}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {errors.drivers && (
                  <p className="text-xs text-red-400">{errors.drivers}</p>
                )}
              </div>
            </div>
          )}

          {/* Driver Companies Selection (when type = 1) */}
          {formData.type === DELIVERY_GROUP_TYPES.DELIVERY_COMPANIES && (
            <div className="border-b border-gray-800 p-4 sm:p-6">
              <h3 className="mb-4 flex items-center text-lg font-semibold text-white">
                <Icon name="Truck" className="mr-2 h-5 w-5" />
                {t("createGroup.sections.selectDeliveryCompanies")}
              </h3>

              <div className="space-y-4">
                <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-700 bg-gray-800 p-4">
                  {driverCompanies.length === 0 ? (
                    <p className="py-4 text-center text-sm text-gray-400">
                      {t("createGroup.states.noDeliveryCompaniesFound")}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {driverCompanies.map((company) => {
                        const isSelected = selectedDriverCompanies.has(
                          company.id,
                        );

                        return (
                          <div
                            key={company.id}
                            className={`flex items-center justify-between rounded-lg border p-3 ${
                              isSelected
                                ? "border-blue-500 bg-blue-500/10"
                                : "border-gray-600 bg-gray-700/50"
                            }`}
                          >
                            <div className="flex flex-1 items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleDriverCompany(company.id)}
                                className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
                              />
                              <div>
                                <p className="text-sm font-medium text-white">
                                  {company.name ||
                                    t("createGroup.fallbacks.companyWithId", {
                                      id: company.id,
                                    })}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {errors.driver_companies && (
                  <p className="text-xs text-red-400">
                    {errors.driver_companies}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Businesses Selection */}
          <div className="p-4 sm:p-6">
            <h3 className="mb-4 flex items-center text-lg font-semibold text-white">
              <Icon name="Store" className="mr-2 h-5 w-5" />
              {t("createGroup.sections.selectBusinesses")}
            </h3>

            <div className="space-y-4">
              <Input
                label={t("createGroup.fields.searchBusinesses.label")}
                value={businessSearchInput}
                onChange={(e) => setBusinessSearchInput(e.target.value)}
                placeholder={t(
                  "createGroup.fields.searchBusinesses.placeholder",
                )}
              />

              <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-700 bg-gray-800 p-4">
                {isLoadingBusinesses ? (
                  <div className="py-4 text-center">
                    <div className="mx-auto h-6 w-6 animate-spin rounded-full border-b-2 border-blue-500"></div>
                    <p className="mt-2 text-sm text-gray-400">
                      {t("createGroup.states.loadingBusinesses")}
                    </p>
                  </div>
                ) : businesses.length === 0 ? (
                  <p className="py-4 text-center text-sm text-gray-400">
                    {t("createGroup.states.noBusinessesFound")}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {businesses.map((business) => {
                      const isSelected = selectedBusinesses.has(business.id);

                      return (
                        <div
                          key={business.id}
                          className={`flex items-center justify-between rounded-lg border p-3 ${
                            isSelected
                              ? "border-blue-500 bg-blue-500/10"
                              : "border-gray-600 bg-gray-700/50"
                          }`}
                        >
                          <div className="flex flex-1 items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleBusiness(business.id)}
                              className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
                            />
                            {business.logo ? (
                              <img
                                src={business.logo}
                                alt={business.name}
                                className="h-8 w-8 rounded object-cover"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-700">
                                <Icon
                                  name="Store"
                                  className="h-4 w-4 text-gray-400"
                                />
                              </div>
                            )}
                            <div>
                              <p className="text-sm font-medium text-white">
                                {business.name}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {errors.business && (
                <p className="text-xs text-red-400">{errors.business}</p>
              )}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={handleCancel}
            disabled={createDeliveryGroupMutation.isPending}
          >
            {t("common:actions.cancel")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={createDeliveryGroupMutation.isPending}
            disabled={createDeliveryGroupMutation.isPending}
          >
            {createDeliveryGroupMutation.isPending
              ? t("createGroup.actions.creating")
              : t("createGroup.actions.create")}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateDeliveryGroupPage;
